import { useEffect, useState } from 'react'
import { createPortal } from 'react-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Avatar } from '@/components/ui/Avatar'
import { createGroup } from '@/api/groups'
import { fetchFriends } from '@/api/users'
import { toFriend } from '@/lib/steam'
import { useUIStore } from '@/store/ui'

const EMOJIS = ['🎮', '🕹️', '⚔️', '🛡️', '🏆', '💎', '🔥', '⚡', '💀', '👾', '🐉', '🍄', '🎯', '🚀', '🧙', '🏹', '🤖', '🎲', '🦊', '🐺']

interface CreateGroupModalProps {
  onClose: () => void
}

export function CreateGroupModal({ onClose }: CreateGroupModalProps) {
  const [name, setName] = useState('')
  const [emoji, setEmoji] = useState('🎮')
  const [selected, setSelected] = useState<Set<string>>(new Set())
  const [search, setSearch] = useState('')

  const queryClient = useQueryClient()
  const { setActiveGroupId } = useUIStore()

  const { data } = useQuery({ queryKey: ['friends'], queryFn: fetchFriends })
  const friends = (data?.friends ?? []).map(toFriend)
  const filtered = friends.filter((f) => {
    const q = search.toLowerCase()
    return (
      (f.nickname ?? f.name).toLowerCase().includes(q) ||
      f.name.toLowerCase().includes(q) ||
      f.handle.toLowerCase().includes(q)
    )
  })

  const { mutate, isPending } = useMutation({
    mutationFn: createGroup,
    onSuccess: (newGroup) => {
      queryClient.invalidateQueries({ queryKey: ['groups'] })
      setActiveGroupId(newGroup.id)
      onClose()
    },
  })

  useEffect(() => {
    function handleKey(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose()
    }
    document.addEventListener('keydown', handleKey)
    return () => document.removeEventListener('keydown', handleKey)
  }, [onClose])

  function toggleFriend(id: string) {
    setSelected((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  function handleSubmit() {
    if (!name.trim() || isPending) return
    mutate({ name: name.trim(), emoji, memberSteamIds: [...selected] })
  }

  return createPortal(
    <div className="cg-modal-backdrop" onClick={onClose}>
      <div className="cg-modal" onClick={(e) => e.stopPropagation()}>
        <div className="cg-modal-header">
          <h2>Create group</h2>
          <button className="cg-icon-btn cg-modal-close-x" onClick={onClose}>✕</button>
        </div>

        <div style={{ overflowY: 'auto', flex: 1 }}>
          <div className="cg-form-row">
            <label>Group name</label>
            <input
              className="cg-input"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Weekend Warriors"
              autoFocus
            />
          </div>

          <div className="cg-form-row">
            <label>Icon</label>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
              {EMOJIS.map((e) => (
                <button
                  key={e}
                  onClick={() => setEmoji(e)}
                  style={{
                    width: 32,
                    height: 32,
                    fontSize: 16,
                    cursor: 'pointer',
                    border: '1px solid',
                    borderRadius: 'var(--radius)',
                    borderColor: emoji === e ? 'var(--accent)' : 'var(--line)',
                    background:
                      emoji === e ? 'oklch(from var(--accent) l c h / 0.15)' : 'var(--bg-2)',
                  }}
                >
                  {e}
                </button>
              ))}
            </div>
          </div>

          <div className="cg-modal-section">Add friends (optional)</div>

          <div style={{ padding: '0 10px' }}>
            <input
              className="cg-input"
              style={{ width: '100%', boxSizing: 'border-box' }}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search friends…"
            />
          </div>

          <div style={{ padding: '8px 10px 16px' }}>
            <div className="cg-pick-grid">
              {filtered.map((f) => (
                <div
                  key={f.id}
                  className="cg-pick-item"
                  data-active={selected.has(f.id)}
                  onClick={() => toggleFriend(f.id)}
                >
                  <Avatar friend={f} size={28} />
                  <div className="cg-pick-body">
                    <div className="cg-pick-name">{f.nickname ?? f.name}</div>
                    <div className="cg-pick-status">@{f.handle}</div>
                  </div>
                  <div className="cg-pick-check">{selected.has(f.id) && '✓'}</div>
                </div>
              ))}
              {filtered.length === 0 && (
                <div
                  style={{
                    gridColumn: 'span 2',
                    padding: '20px 0',
                    textAlign: 'center',
                    color: 'var(--fg-3)',
                    fontSize: 12,
                  }}
                >
                  {friends.length === 0 ? 'No friends synced yet' : 'No friends match'}
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="cg-modal-footer">
          <button className="cg-btn ghost" onClick={onClose}>
            Cancel
          </button>
          <button
            className="cg-btn primary"
            disabled={!name.trim() || isPending}
            onClick={handleSubmit}
          >
            {isPending ? 'Creating…' : 'Create group'}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  )
}
